/**
 * Entra ID (Microsoft Identity) auth client.
 *
 * Hybrid login: this module sits alongside SuperTokens — never replaces
 * it. Activated by `VITE_ENTRA_ENABLED=true` + non-empty CLIENT_ID /
 * TENANT_ID. The backend (jwtAuth.ts) accepts both issuers, so the API
 * layer just needs to attach the right Bearer token based on which
 * provider the user is signed in with.
 *
 * Flow (OAuth 2.0 authorization code, no MSAL needed):
 *
 *   1. User clicks "Sign in with Microsoft" → loginWithEntra()
 *      builds the Entra authorize URL with a generated `state` + the
 *      redirect_uri pointing back at our /login page, then
 *      window.location → Microsoft.
 *   2. Microsoft authenticates the user, redirects back to /login with
 *      ?code=<authz_code>&state=<our_state>.
 *   3. LoginView.vue detects those query params on mount and calls
 *      handleEntraCallback(code, state), which posts
 *      the code to backend /api/auth/entra/token. The backend holds
 *      the client secret and exchanges the code for {id_token,
 *      access_token, refresh_token, expires_in, user}. Tokens are stored
 *      in localStorage so they survive a refresh.
 *   4. Subsequent API calls read the id_token via getEntraIdToken()
 *      and attach it as Authorization: Bearer … (id_token, not
 *      access_token — see EntraSession below for why).
 *   5. When the id_token is within REFRESH_LEAD_MS of expiring,
 *      getEntraIdToken() transparently refreshes via
 *      /api/auth/entra/refresh. Expiry is read from the id_token's own
 *      `exp` claim, NOT from `expires_in` — see computeSessionExpiry().
 *
 * Logout drives the user through Microsoft's full sign-out so a shared
 * device can't reuse the SSO cookie — matches the "Full Entra logout"
 * choice we settled on.
 *
 * Storage layout (`localStorage`):
 *   eventhub.entra.session  — { access_token, refresh_token, expires_at, user }
 *   eventhub.entra.state    — short-lived state value sent in the
 *                              authorize redirect; checked on callback
 *                              to defeat CSRF.
 *
 * Security notes:
 *  - localStorage is XSS-readable. Same risk class as SuperTokens'
 *    header-mode session, which already stores its JWT there. We don't
 *    introduce a new risk by storing here.
 *  - The client secret never reaches the browser — backend holds it.
 *  - `state` is a freshly generated UUID per login attempt.
 */

import { backendApi } from './backendApiService'
import { uuid } from '@/utils/uuid'
import { reportSessionWipe, type SessionWipeReason, type SessionWipeContext } from '@/utils/sessionWipeReason'

const STORAGE_KEY = 'eventhub.entra.session'
const STATE_KEY = 'eventhub.entra.state'
// Survives local logout. Lets /login show a "Continue as <name>" CTA so
// a user whose MS SSO cookie is still alive can resume in one click —
// no need to re-type credentials. Cleared explicitly via
// forgetLastEntraIdentity() when the user picks "use a different account".
const LAST_IDENTITY_KEY = 'eventhub.entra.lastIdentity'
const REFRESH_LEAD_MS = 60_000 // refresh when <60s left on id token

export interface EntraLastIdentity {
  email: string
  name: string
  savedAt: number
}

export interface EntraSession {
  // We carry the id_token (not access_token) because the backend's
  // jwtAuth.ts validates aud === ENTRA_CLIENT_ID — only id_tokens have
  // that audience under plain OIDC scopes. The access_token Entra
  // would issue with these scopes is for Microsoft Graph, useless here.
  id_token: string
  refresh_token?: string
  expires_at: number // epoch ms
  user?: {
    id?: string
    email?: string
    name?: string
    roles?: string[]
    [k: string]: any
  }
}

export interface EntraConfig {
  enabled: boolean
  clientId: string
  tenantId: string
}

export function getEntraConfig(): EntraConfig {
  const rawEnabled = import.meta.env.VITE_ENTRA_ENABLED
  const clientId = String(import.meta.env.VITE_ENTRA_CLIENT_ID || '').trim()
  const tenantId = String(import.meta.env.VITE_ENTRA_TENANT_ID || '').trim()
  // Treat unsubstituted placeholders the same as missing.
  const isPlaceholder = (v: string) => v.startsWith('__VITE_') && v.endsWith('__')
  const enabled =
    rawEnabled === 'true' &&
    !!clientId && !isPlaceholder(clientId) &&
    !!tenantId && !isPlaceholder(tenantId)
  return { enabled, clientId, tenantId }
}

function readSession(): EntraSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.id_token || !parsed?.expires_at) return null
    return parsed as EntraSession
  } catch {
    return null
  }
}

function writeSession(s: EntraSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch { /* quota / private mode — non-fatal */ }
}

// The last reason THIS tab removed the session bytes. Lets callers that
// only observe "hasEntraSession() went false" (auth store, db-jwt refresh)
// forward the real cause instead of guessing.
let lastWipeReason: SessionWipeReason | null = null

/**
 * The ONLY way the session bytes leave localStorage. Every caller names
 * why; the wipe is reported once, and only when there was a session to
 * wipe — the /login flows call this defensively on an already-empty store
 * and must not spam telemetry.
 */
function wipeSession(reason: SessionWipeReason, context: SessionWipeContext = {}): void {
  const existing = readSession()
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch { /* private mode — non-fatal */ }
  if (!existing) return
  lastWipeReason = reason
  reportSessionWipe(reason, {
    ms_to_expiry: existing.expires_at - Date.now(),
    had_refresh_token: !!existing.refresh_token,
    ...context,
  })
}

/** Why this tab last wiped the session, or null if it never did. */
export function getLastEntraWipeReason(): SessionWipeReason | null {
  return lastWipeReason
}

/**
 * `fetch` rejects with a TypeError when the network is unreachable
 * ("Failed to fetch" / "NetworkError when attempting to fetch resource." /
 * "Load failed"). That is the ordinary shape of an outage, not a bug.
 */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && /fetch|network|load failed/i.test(err.message)
}

/** Read the persisted "last signed in" identity, if any. */
export function getLastEntraIdentity(): EntraLastIdentity | null {
  try {
    const raw = localStorage.getItem(LAST_IDENTITY_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.email && !parsed?.name) return null
    return parsed as EntraLastIdentity
  } catch {
    return null
  }
}

/** Forget the persisted identity — used by "use a different account". */
export function forgetLastEntraIdentity(): void {
  try { localStorage.removeItem(LAST_IDENTITY_KEY) } catch { /* ignore */ }
}

function writeLastIdentity(user: EntraSession['user']): void {
  if (!user?.email && !user?.name) return
  try {
    localStorage.setItem(LAST_IDENTITY_KEY, JSON.stringify({
      email: user.email || '',
      name: user.name || '',
      savedAt: Date.now(),
    } satisfies EntraLastIdentity))
  } catch { /* ignore */ }
}

/**
 * Decode the payload of a JWT without verifying it. Safe here because
 * we just received the token from our own backend over HTTPS — we're
 * only reading claims (email/name/oid) for UI state, not making any
 * security decision. Returns null on malformed input.
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

/**
 * When does the session we just received actually stop working?
 *
 * Subtle, and it bit us in production: Entra's `expires_in` describes the
 * **access_token**, but the token we put in the Authorization header is the
 * **id_token** (see EntraSession above — the backend validates
 * aud === ENTRA_CLIENT_ID). Azure applies token-lifetime variation to each
 * token independently, so those two expiries routinely differ: a real capture
 * had id_token = 60min and expires_in = ~84min. Trusting `expires_in` left a
 * 24-minute window where getEntraIdToken() happily returned an id_token Entra
 * had already expired — every call 401'd, and the 401 handler logged the user
 * out without ever spending the (perfectly valid) refresh_token.
 *
 * So: prefer the id_token's own `exp`, and take the earlier of the two when
 * both are known. `expires_in` is only the fallback for a malformed/claimless
 * token. SKEW_MS trims a little off so we start refreshing just before the
 * hard boundary rather than racing it.
 */
const EXPIRY_SKEW_MS = 30_000

export function computeSessionExpiry(idToken: string, expiresInSeconds?: number): number {
  const now = Date.now()
  const candidates: number[] = []

  const exp = decodeJwtPayload(idToken)?.exp
  if (typeof exp === 'number' && Number.isFinite(exp)) candidates.push(exp * 1000)
  if (expiresInSeconds && expiresInSeconds > 0) candidates.push(now + expiresInSeconds * 1000)

  // Nothing usable (malformed token AND no expires_in) — treat as immediately
  // stale so the next call refreshes rather than trusting an unknown lifetime.
  if (candidates.length === 0) return now

  return Math.max(now, Math.min(...candidates) - EXPIRY_SKEW_MS)
}

/**
 * Kick off the OAuth code flow. Stashes a state nonce in localStorage
 * so the callback page can verify it, then full-page redirects to
 * Microsoft's authorize endpoint.
 *
 * Options:
 *   loginHint           — pre-select an account by email. When the user
 *                         already has an active MS SSO session matching
 *                         that hint, Microsoft skips the chooser and
 *                         returns to us silently. Used by the "Continue
 *                         as <name>" resume card on /login.
 *   forceSelectAccount  — always show the account picker, even if SSO
 *                         is active. Used by the explicit "Sign in
 *                         with a different account" affordance.
 *
 * With neither set, Microsoft picks the smart default (silent if a
 * single account is signed in, chooser if multiple). The /login UI
 * exposes both explicit paths so users on shared devices aren't
 * surprised by an auto-resume.
 */
export function loginWithEntra(opts: { loginHint?: string; forceSelectAccount?: boolean } = {}): void {
  const cfg = getEntraConfig()
  if (!cfg.enabled) {
    console.warn('[entra] login attempted but VITE_ENTRA_ENABLED is not true')
    return
  }
  const state = uuid()
  try { localStorage.setItem(STATE_KEY, state) } catch { /* ignore */ }

  const redirectUri = `${window.location.origin}/login`
  const scope = 'openid profile email offline_access'
  const url = new URL(`https://login.microsoftonline.com/${cfg.tenantId}/oauth2/v2.0/authorize`)
  url.searchParams.set('client_id', cfg.clientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('response_mode', 'query')
  url.searchParams.set('scope', scope)
  url.searchParams.set('state', state)
  if (opts.loginHint) url.searchParams.set('login_hint', opts.loginHint)
  if (opts.forceSelectAccount) url.searchParams.set('prompt', 'select_account')
  window.location.assign(url.toString())
}

/**
 * Complete the OAuth handshake. Verifies the state nonce, posts the
 * code to backend /api/auth/entra/token, stores the resulting session.
 * Returns the user object so the caller can drive routing/identity.
 */
export async function handleEntraCallback(code: string, state: string): Promise<EntraSession['user']> {
  let expectedState: string | null = null
  try { expectedState = localStorage.getItem(STATE_KEY) } catch { /* ignore */ }
  try { localStorage.removeItem(STATE_KEY) } catch { /* ignore */ }
  if (!expectedState || expectedState !== state) {
    throw new Error('Entra callback: state mismatch (possible CSRF or stale tab)')
  }

  // Static import at module top — no circular dep (backendApiService
  // doesn't reach back here) and avoids the Vite "dynamic import will
  // not move module into another chunk" warning at build time.
  const redirectUri = `${window.location.origin}/login`

  // The backend route owns the client secret and does the actual
  // token exchange with Microsoft. We just hand over the code +
  // redirect_uri (Microsoft requires this match the authorize call).
  const response = await backendApi.request<{
    access_token: string
    id_token: string
    refresh_token?: string
    expires_in: number // seconds
    user?: EntraSession['user']
  }>('/api/auth/entra/token', {
    method: 'POST',
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })

  if (!response.id_token) {
    throw new Error('Entra token response missing id_token — check scope includes openid')
  }

  // Backend currently returns just tokens, not a parsed user payload.
  // Decode the id_token to populate the user-shaped fields the auth
  // store and UI read (email/name/oid). preferred_username is the
  // UPN-form login string Entra exposes; falling back to it keeps us
  // resilient to tenants that withhold the `email` claim.
  const claims = decodeJwtPayload(response.id_token) || {}
  const user = response.user ?? {
    id: claims.oid || claims.sub,
    email: claims.email || claims.preferred_username,
    name: claims.name,
    roles: Array.isArray(claims.roles) ? claims.roles : [],
  }

  const expires_at = computeSessionExpiry(response.id_token, response.expires_in)
  const session: EntraSession = {
    id_token: response.id_token,
    refresh_token: response.refresh_token,
    expires_at,
    user,
  }
  writeSession(session)
  // Persist a lightweight "last identity" so /login can offer a
  // one-click resume on the next visit. Survives signOutEntra().
  writeLastIdentity(user)
  return session.user
}

/** Is there an Entra session in localStorage (regardless of expiry)? */
export function hasEntraSession(): boolean {
  return !!readSession()
}

/**
 * Returns the stored session's expires_at (epoch ms) without triggering
 * a refresh. null when no session is stored. Used by the auth store's
 * watchdog to cheaply check expiry on a poll without spamming the
 * refresh endpoint every minute.
 */
export function getEntraSessionExpiresAt(): number | null {
  return readSession()?.expires_at ?? null
}

/** Currently stored user payload (email/name/roles). */
export function getEntraUser(): EntraSession['user'] | null {
  return readSession()?.user || null
}

let refreshInflight: Promise<string | null> | null = null

/**
 * Return a usable id_token, refreshing transparently when within
 * REFRESH_LEAD_MS of expiry. Returns null when no session exists or the
 * refresh fails — callers should treat that as "not signed in via Entra"
 * and fall back to their other auth source.
 *
 * `force: true` skips the "still fresh" shortcut and always spends the
 * refresh_token. Used by the 401 recovery path: if the backend rejected a
 * token we believed was healthy, our expiry bookkeeping is what's wrong, so
 * asking Entra is the only way to find out the truth.
 */
export async function getEntraIdToken(opts: { force?: boolean } = {}): Promise<string | null> {
  const session = readSession()
  if (!session) return null
  if (!opts.force && session.expires_at - Date.now() > REFRESH_LEAD_MS) {
    return session.id_token
  }
  if (!session.refresh_token) {
    // No refresh token to recover with — session is dead.
    wipeSession('no_refresh_token')
    return null
  }
  if (!refreshInflight) {
    // `Promise.resolve().then(...)` — NOT an immediately-invoked async
    // function. The body must not start until AFTER `refreshInflight` has
    // been assigned, or the guard cannot guard: an async IIFE runs
    // synchronously up to its first await, so it reached backendApi.request
    // (and from there back into this function) while `refreshInflight` was
    // still null, and recursed until the stack overflowed. Deferring to a
    // microtask makes the assignment land first, so any re-entrant caller
    // awaits this promise instead of starting a second refresh.
    refreshInflight = Promise.resolve().then(async () => {
      try {
        // Static import at module top. NOTE: backendApiService DOES reach
        // back here via getUpstreamJwt() — it just skips the call for
        // /api/auth/entra/refresh (see PRE_SESSION_ROUTES there), which is
        // what keeps this from being a cycle. An earlier comment claimed no
        // circular dep; that assumption is what broke.
        const r = await backendApi.request<{
          access_token: string
          id_token: string
          refresh_token?: string
          expires_in: number
        }>('/api/auth/entra/refresh', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: session.refresh_token }),
        })
        if (!r.id_token) {
          throw new Error('Entra refresh response missing id_token')
        }
        const next: EntraSession = {
          id_token: r.id_token,
          refresh_token: r.refresh_token || session.refresh_token,
          expires_at: computeSessionExpiry(r.id_token, r.expires_in),
          user: session.user,
        }
        writeSession(next)
        return next.id_token
      } catch (err) {
        // Only a DEFINITIVE rejection of the refresh_token (400/401 from
        // /api/auth/entra/refresh) means the session is dead — wipe it so the
        // caller bounces to /login. A TRANSIENT failure (network blip, 429
        // rate-limit, 5xx, timeout) must NOT wipe a valid session: doing so
        // turned a hiccup during a best-effort MEWS call (e.g. the rates tab
        // firing rates/getAll near token expiry) into a full logout on the very
        // next authenticated request — the "rates tab randomly kicks me out"
        // report. Keep the session so the next call simply retries the refresh.
        //
        // A network-unreachable TypeError is the most ordinary transient of
        // all — fetch throws it on every outage. It used to be filed under
        // "programming error" below, which logged a scary BUG line on every
        // load-shedding blip and sent diagnosis down the wrong path. Keep the
        // session; the next call retries.
        if (isNetworkError(err)) {
          console.warn('[entra] refresh failed — network unreachable, keeping session for retry', err)
          return null
        }
        // A RangeError/other TypeError IS a bug in our own code, and filing
        // it under "transient" is how the refresh recursion stayed invisible
        // for weeks — a stack overflow was logged as a network blip and
        // retried on every subsequent call, with every one of those calls
        // going out unauthenticated. Still returns null (three callers depend
        // on "null + session bytes present = transient"), but it must be loud.
        if (err instanceof RangeError || err instanceof TypeError) {
          console.error(
            '[entra] refresh hit a programming error — this is a BUG, not a transient failure',
            err,
          )
          return null
        }
        const status = (err as { status?: number } | null)?.status
        if (status === 400 || status === 401) {
          console.warn('[entra] refresh rejected — session is dead, clearing', err)
          wipeSession('refresh_rejected', { status })
        } else {
          console.warn('[entra] refresh failed transiently — keeping session for retry', err)
        }
        return null
      } finally {
        refreshInflight = null
      }
    })
  }
  return refreshInflight
}

/**
 * Sign out of Entra LOCALLY. Clears our session but leaves the
 * Microsoft SSO cookie alone — by design. The /login screen detects
 * this via getLastEntraIdentity() and offers a "Continue as <name>"
 * shortcut that resumes without a credential prompt.
 *
 * For the rare "I'm on a shared device, fully sign me out of MS too"
 * case, call signOutEntraFully() instead (currently unused by the
 * default user-menu flow but available for future UI).
 */
export function signOutEntra(): void {
  wipeSession('user_signout')
}

/**
 * Same as signOutEntra() but also redirects through Microsoft's
 * logout endpoint to invalidate the SSO cookie. Use when the user
 * explicitly asks to sign out of Microsoft, e.g. on a shared device.
 */
export function signOutEntraFully(): void {
  wipeSession('user_signout')
  forgetLastEntraIdentity()
  const cfg = getEntraConfig()
  if (!cfg.enabled) return
  const postLogout = `${window.location.origin}/login`
  const url = new URL(`https://login.microsoftonline.com/${cfg.tenantId}/oauth2/v2.0/logout`)
  url.searchParams.set('post_logout_redirect_uri', postLogout)
  window.location.assign(url.toString())
}

/**
 * Clear the local session WITHOUT bouncing through Microsoft. Used by
 * the auth store on programmatic cleanup (e.g. token-expired recovery)
 * where redirecting would interrupt the user mid-task. The reason is
 * mandatory — see sessionWipeReason.ts for why.
 */
export function clearLocalEntraSession(reason: SessionWipeReason): void {
  wipeSession(reason)
}
